import { Component, type ErrorInfo, type ReactNode } from 'react'
import { captureRenderError } from '@/shared/observability/sentry'

export interface ErrorBoundaryProps {
  children: ReactNode
  fallback: ReactNode | ((error: Error) => ReactNode)
}

interface ErrorBoundaryState {
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureRenderError(error, info.componentStack)
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, info.componentStack)
    }
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error) {
      const { fallback } = this.props
      return typeof fallback === 'function' ? fallback(error) : fallback
    }
    return this.props.children
  }
}
